import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_ACCESS = "bni-scanner-access";
const KEY_REFRESH = "bni-scanner-refresh";
const KEY_EVENT = "bni-scanner-event";
const KEY_NAME = "bni-scanner-name";
const EVENT_SLUG = "davangere-members-day-2026";

export const scannerAuth = {
  get token() { return localStorage.getItem(KEY_ACCESS); },
  get refreshToken() { return localStorage.getItem(KEY_REFRESH); },
  get eventId() { return localStorage.getItem(KEY_EVENT); },
  get userName() { return localStorage.getItem(KEY_NAME); },
  clear() { [KEY_ACCESS, KEY_REFRESH, KEY_EVENT, KEY_NAME].forEach((k) => localStorage.removeItem(k)); },
  async login(phone, password) {
    const r = await axios.post(`${API}/scanner/auth/login`, { phone, password, event_slug: EVENT_SLUG });
    localStorage.setItem(KEY_ACCESS, r.data.access);
    localStorage.setItem(KEY_REFRESH, r.data.refresh);
    localStorage.setItem(KEY_EVENT, r.data.event_id);
    localStorage.setItem(KEY_NAME, r.data.user_name || "");
    return r.data;
  },
  async refresh() {
    const rt = this.refreshToken;
    if (!rt) throw new Error("no refresh token");
    const r = await axios.post(`${API}/scanner/auth/refresh`, {}, { headers: { Authorization: `Bearer ${rt}` } });
    localStorage.setItem(KEY_ACCESS, r.data.access);
    return r.data;
  },
  // Exchange a signed-in admin JWT for a scanner token — lets staff open
  // Reception / Scanner from the admin sidebar without a second password prompt.
  async exchangeFromAdmin() {
    const adminTok = localStorage.getItem("bni-admin-token") || localStorage.getItem("bni-devadmin-token");
    if (!adminTok) throw new Error("no admin token");
    const r = await axios.post(`${API}/scanner/auth/exchange`, null, {
      params: { event_slug: EVENT_SLUG },
      headers: { Authorization: `Bearer ${adminTok}` },
    });
    localStorage.setItem(KEY_ACCESS, r.data.access);
    localStorage.setItem(KEY_REFRESH, r.data.refresh);
    localStorage.setItem(KEY_EVENT, r.data.event_id);
    localStorage.setItem(KEY_NAME, r.data.user_name || "");
    return r.data;
  },
};

function headers() { const t = scannerAuth.token; return t ? { Authorization: `Bearer ${t}` } : {}; }

async function withAuth(fn) {
  try { return await fn(); }
  catch (e) {
    if (e?.response?.status === 401 && scannerAuth.refreshToken) {
      try { await scannerAuth.refresh(); return await fn(); }
      catch { scannerAuth.clear(); throw e; }
    }
    throw e;
  }
}

export const scannerApi = {
  mealWindows: () => withAuth(() => axios.get(`${API}/scanner/meal-windows`, { headers: headers() }).then((r) => r.data)),
  checkin: (body) => withAuth(() => axios.post(`${API}/scanner/checkin`, body, { headers: headers() }).then((r) => r.data)),
  recent: (limit = 20) => withAuth(() => axios.get(`${API}/scanner/recent`, { params: { limit }, headers: headers() }).then((r) => r.data)),
  entryStats: (limit = 12) => withAuth(() => axios.get(`${API}/scanner/entry/stats`, { params: { limit }, headers: headers() }).then((r) => r.data)),
  entrySearch: (q) => withAuth(() => axios.get(`${API}/scanner/entry/search`, { params: { q }, headers: headers() }).then((r) => r.data)),
  entryManual: (registration_id) => withAuth(() => axios.post(`${API}/scanner/entry/manual`, { registration_id }, { headers: headers() }).then((r) => r.data)),
  entryUndo: (registration_id) => withAuth(() => axios.delete(`${API}/scanner/entry/${registration_id}`, { headers: headers() }).then((r) => r.data)),
};

// ---------- Offline queue (localStorage-backed, keyed for dedup) ----------
const QKEY = "bni-scanner-queue";
function loadQueue() { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } }
function saveQueue(q) { localStorage.setItem(QKEY, JSON.stringify(q)); }

export const offlineQueue = {
  size() { return loadQueue().length; },
  enqueue(item) {
    // dedupe on (qr_token, meal_window_id)
    const q = loadQueue();
    const key = `${item.qr_token}::${item.meal_window_id}`;
    if (q.some((x) => `${x.qr_token}::${x.meal_window_id}` === key)) return false;
    q.push({ ...item, queued_at: Date.now() });
    saveQueue(q);
    return true;
  },
  async drain(onResult) {
    const q = loadQueue();
    if (q.length === 0) return { processed: 0 };
    const remaining = [];
    let processed = 0;
    for (const item of q) {
      try {
        const r = await scannerApi.checkin(item);
        onResult?.(r, item);
        processed += 1;
      } catch { remaining.push(item); }
    }
    saveQueue(remaining);
    return { processed, remaining: remaining.length };
  },
  clear() { saveQueue([]); },
};
